import React from "react";
import type { AlertEvent } from "../../hooks/useThermalAlerts";
import type { HeatmapRow } from "../../hooks/ThermalHeatMapHooks";
import { formatTime } from "../../hooks/ThermalHeatMapHooks";
import { getSubsystemTemperatures, formatSubsystemDescription } from "../../hooks/useThermalAlerts";

export interface AlertDescriptionRowProps {
  alert: AlertEvent;
  heatmapRow?: HeatmapRow;
  thresholdValue: number;
}

const AlertDescriptionRow: React.FC<AlertDescriptionRowProps> = ({ 
  alert, 
  heatmapRow,
  thresholdValue 
}) => {
  const subsystemTemps = getSubsystemTemperatures(
    alert.face,
    heatmapRow,
    thresholdValue,
    alert.breach
  );

  if (subsystemTemps.length === 0) {
    return (
      <tr className="thm-alert-description-row">
        <td colSpan={7}>
          <div className="thm-alert-description">
            <p className="thm-alert-description-empty">No subsystems configured on this face</p>
          </div>
        </td>
      </tr>
    );
  }

  const breachingSubsystems = subsystemTemps.filter((s) => s.isBreaching);
  const nonBreachingSubsystems = subsystemTemps.filter((s) => !s.isBreaching);

  return (
    <tr className="thm-alert-description-row">
      <td colSpan={7}>
        <div className="thm-alert-description">
          <div className="thm-alert-description-header">
            <strong>Subsystems on {alert.faceLabel} (Peak: {alert.peakTemp.toFixed(1)}°C at {formatTime(alert.timeSec)})</strong>
          </div>
          
          {breachingSubsystems.length > 0 && (
            <div className="thm-alert-description-section">
              <div className="thm-alert-description-subtitle">⚠️ Breaching Threshold:</div>
              <ul className="thm-alert-description-list">
                {breachingSubsystems.map((subsys, idx) => (
                  <li key={`${subsys.nodeId}-${idx}`} className="thm-alert-description-item thm-alert-description-item--breach">
                    {formatSubsystemDescription(subsys, alert.breach)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nonBreachingSubsystems.length > 0 && (
            <div className="thm-alert-description-section">
              <div className="thm-alert-description-subtitle">✓ Within Threshold:</div>
              <ul className="thm-alert-description-list">
                {nonBreachingSubsystems.map((subsys, idx) => (
                  <li key={`${subsys.nodeId}-${idx}`} className="thm-alert-description-item">
                    {formatSubsystemDescription(subsys, alert.breach)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default AlertDescriptionRow;
